import React from 'react';
import I18n from 'i18n-js';
import { DataValue } from 'react-apollo';
import gql from 'graphql-tag';
import {
  pick,
  union,
  differenceWith,
  isEqual,
  keyBy,
  compact,
  omit,
  reject,
  castArray,
  uniq,
} from 'lodash';
import qs from 'qs';
import { withRouter, WithRouterProps } from 'react-router';

import { LISTING_STATES } from './shared/constants';
import experiments from '@reverbdotcom/commons/src/experiments';
import bind from '@reverbdotcom/commons/src/bind';
import { CSP2FiltersOption } from './csp2_filters';
import CSP2CSPReviews from './csp2_csp_reviews';
import CSP2Header from './csp2_header';
import CSP2Jumplinks, {
  IJumplinkProps,
  JUMPLINK_ORDER,
  JUMPLINK_SIMILAR,
  JUMPLINK_PRICEGUIDE,
  JUMPLINK_REVIEWS,
  JUMPLINK_BLURB,
  JUMPLINK_LISTINGS,
  CSPJumplinksFragment,
} from './csp2_jumplinks';
import CSP2Sidebar from './csp2_sidebar';
import CSP2Metadata from './csp2_metadata';
import {
  core_apimessages_ProductSpec_Type,
  CoreCspLayoutQuery,
  CspFiltersAndListingsQuery,
  CSP,
  Listing,
  reverb_search_ListingsSearchRequest_Aggregation as Aggregation,
  reverb_search_ListingsSearchRequest_Sort,
  arbiter_AuctionType,
} from '../schemas/rql';
import { Paths, withWebHost } from './shared/url_helpers';
import relatedBuyingGuide from './related_buying_guide';
import { getOffsetFromPage } from '../pagination_helpers';
import CSP2FeaturedListing, { CSP2FeaturedListingFragment } from './csp2_featured_listing';
import RecentlyViewedCspsRow from './recently_viewed_csps_row';
import { CSP2EmptyState } from './csp2_empty_state';
import { CSP2FeedCallout } from './csp2_feed_callout';
import AppInstallCallout from './app_install_callout';
import { CSPProductFamilyCTA, CSPProductFamilyCTAFragment } from './csp_product_family_cta';
import CSPPriceGuideModuleContainer from './price_guide_tool/csp_module/csp_price_guide_module_container';
import SellYoursV4 from './sell_yours_v4';
import { isUSA } from '../lib/geo';

import { flashSuccess } from '@reverbdotcom/commons/src/flash';
import SeoBlurb from '@reverbdotcom/commons/src/cms/components/seo_blurb';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import {
  isExperimentEnabled,
  canViewFinancingOptions,
} from '@reverbdotcom/commons/src/user_context_helpers';
import { getCategoryAttributesForMparticle } from '@reverbdotcom/commons/src/csp_category_helper';
import { CSPAdditionalTrackingFieldsFragment } from '@reverbdotcom/commons/src/components/csp_row';
import { fullFinancingFieldsFragment } from '@reverbdotcom/commons/src/components/affirm_estimate_listing_card_cta';
import SimilarCspsRow from '@reverbdotcom/commons/src/components/similar_csps_row';
import { ListingsCollection as ListingsCollectionClass } from '@reverbdotcom/commons/src/components/listings_collection';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { MParticlePageName, trackPageView, trackEvent, MParticleEventName, PageViewEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes, userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { SellerInfoFragment } from '@reverbdotcom/commons/src/components/seller_info';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import { trackFacebookEvent, VIEW_CONTENT_EVENT, PRODUCT_CONTENT_TYPE } from '@reverbdotcom/commons/src/facebook';
import { TruncationContainer } from '@reverbdotcom/commons/src/components/truncation_container';
import { nestedFilterFragment } from '@reverbdotcom/commons/src/components/rql_facets';
import MarketplaceFilterRow from '@reverbdotcom/commons/src/components/marketplace_filter_row';
import { SortChip } from '@reverbdotcom/commons/src/components/rql_search_overview';
import { WidgetType } from '@reverbdotcom/commons/src/components/search_filters';
import toBool from '../reverb-js-common/to_boolean';
import { mParticleListingFields } from '@reverbdotcom/commons/src/components/with_mparticle_listings';
import { listingsFilters } from './listings_filters';
import { trackLocalizeCSPCardExperiment } from '@reverbdotcom/commons/src/seller_location_experiments';
import { localizeCSPForUser, localizeCSPItemRegion } from '@reverbdotcom/commons/src/csp2_layout_filters';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { isCoreBrandNew, CONDITION_GROUPS } from '@reverbdotcom/commons/src/condition_helpers';
import { ListingGreatValueFragment } from '@reverbdotcom/commons/src/listing_great_value';
import { connect } from '@reverbdotcom/commons/src/connect';

const LISTINGS_LIMIT = 12;
const BUMP_LIMIT = 2;
export const SPONSORED_LISTINGS_LIMIT = 2;

const SHOW_FEATURED_IN_COLLECTION_LIMIT = 6;

// fixed cutoff at which point we render a 'view more'
// we have a general sense of how tall listing cards are
// so this suffices and keeps us from having to do
// dynamic height detection within TruncationContainer
const LISTING_TRUNCATION_THRESHOLD = 6;
const LISTING_TRUNCATION_REM_HEIGHT = 80;

const ALL_TOGETHER = 'allListings';
const ALL_BUMPED = 'allBumped';

const FRETBOARD_MATERIAL_TRAIT_UUID = '6a24e2dc-800d-4f06-ac67-acef9569a40f';
const APPROVED_FRETBOARD_FILTER_CATEGORIES = [
  'dfd39027-d134-4353-b9e4-57dc6be791b9', // electric guitars
  '3ca3eb03-7eac-477d-b253-15ce603d2550', // acoustic guitars
  '53a9c7d7-d73d-4e7f-905c-553503e50a90', // bass guitars
];

const COMPONENT_NAME = 'CspListings';
const TRUNCATION_CONTAINER_TRACKING_NAME = 'CspShowMoreButton';

type ExternalProps = IUserContext & WithRouterProps;

interface IState extends IJumplinkProps {
  featuredListings: { [s: string]: Listing };
}

interface CustomDataProps {
  layoutData: DataValue<CoreCspLayoutQuery.Query, CoreCspLayoutQuery.Variables>;
  filtersAndListingsData: DataValue<CspFiltersAndListingsQuery.Query, CspFiltersAndListingsQuery.Variables>;
}

type IProps = ExternalProps & CustomDataProps;

function followParams(
  params: WithRouterProps['params'],
  location: WithRouterProps['location'],
) {
  return {
    comparison_shopping_page_slug: params?.slug,
    follow_prompt: location?.query?.follow_prompt,
  };
}

function hideBumpedAndSponsoredListings({ pageNumber, sortQuery }) {
  return parseInt(pageNumber, 10) > 1 || !!sortQuery;
}

export class CSP2Layout extends React.Component<IProps, IState> {
  scrollTarget: ScrollTarget = null;

  state = {
    featuredListings: {},
  };

  componentDidMount() {
    if (this.scrollTarget) {
      this.scrollTarget.scrollToTop();
    }

    this.logView();
  }

  componentDidUpdate(oldProps: IProps) {
    if (this.scrollTarget &&
      (this.props.params.slug !== oldProps.params.slug)) {
      this.scrollTarget.scrollToTop();
    }

    if (!this.queryDataLoading(this.props) && this.queryDataLoading(oldProps)) {
      this.logView();
    }

    this.tryCacheFeaturedListing();
  }

  tryCacheFeaturedListing() {
    const featuredListing = this.calculateFeaturedListing();

    const key = this.featuredListingKey();
    const cached = this.state.featuredListings[key];

    const isCacheable = featuredListing && !cached && !this.props.layoutData?.loading && !this.queryForAdditionalFeaturedListingFields();

    const isListingDirty = featuredListing && cached && featuredListing.id === cached.id && !isEqual(featuredListing, cached);

    if (!isCacheable && !isListingDirty) {
      return;
    }

    this.setState(old => ({
      featuredListings: {
        ...old.featuredListings,
        [key]: featuredListing,
      },
    }));
  }

  featuredListingKey() {
    return `${this.props.location?.pathname}${qs.stringify(omit(this.props.location?.query, 'page'))}`;
  }

  canonicalFinish() {
    return this.props.location?.query?.canonicalFinish || '';
  }

  fretboardMaterial() {
    return this.props.location?.query?.traitValues || '';
  }

  featuredListingData(): Partial<PageViewEvent> {
    const featured = this.featuredListing();

    if (featured) {
      return {
        featuredListingCondition: isCoreBrandNew(featured.condition.conditionUuid) ? 'new' : 'used',
        featuredListingConditionSlug: featured.condition.conditionSlug,
      };
    }

    return {};
  }

  queryDataLoading(props) {
    return props.layoutData?.loading || props.filtersAndListingsData?.loading;
  }

  logView() {
    if (this.queryDataLoading(this.props)) return;

    trackPageView({
      pageName: MParticlePageName.CSP,
      cspSlug: this.props.params.slug,
      cspId: this.csp?.id,
      brand: this.csp?.brand?.slug,
      finishCount: this.csp?.finishes?.length,
      listingsCount: this.allCurrentListingsBeingDisplayed().length,
      listingIds: this.nonBumpedListings()?.map(l => l.id),
      bumpedListingIds: this.sponsoredListings()?.filter(l => l.bumped)?.map(l => l.id),
      sponsoredListingIds: this.sponsoredListings()?.map(l => l.id),
      ...this.featuredListingData(),
      experiments: formatExperimentsForEventAttributes(this.props.user, [
        experiments.DUMMY,
        experiments.MV_DUMMY,
        experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
        experiments.PRICE_DROP_FILTER,
        experiments.CSP_FEATURED_LISTINGS_UPDATE,
        ...trackLocalizeCSPCardExperiment(this.props.user),
      ]),
      ...getCategoryAttributesForMparticle(this.csp),
    });

    this.trackListings();
  }

  @bind
  setJumplinks(jumplinks) {
    const newState = { ...this.state, ...jumplinks };
    if (!isEqual(this.state, newState)) {
      this.setState(newState);
    }
  }

  getJumplinks() {
    const jumpLinks = {
      [JUMPLINK_LISTINGS]: !this.noListings(),
      [JUMPLINK_PRICEGUIDE]: true,
      [JUMPLINK_REVIEWS]: true,
      ...this.state,
    };

    return pick(jumpLinks, ...JUMPLINK_ORDER) as IJumplinkProps;
  }

  get csp(): CSP {
    return this.props.layoutData?.csp;
  }

  getListings(name: string) {
    return this.props.filtersAndListingsData[name]?.listings || [];
  }

  get fallbackListings() {
    return this.props.filtersAndListingsData?.allListings?.fallbackListings || [];
  }

  hasListings() {
    return (this.props.filtersAndListingsData?.allListings?.total || 0) > 0;
  }

  noListings() {
    if (this.props.filtersAndListingsData?.loading) return false;
    if (!this.props.filtersAndListingsData?.allListings) return false;
    if (this.hfid) return false;
    if (this.featuredListing()) return false;

    const totalNonbumpedListings = this.props.filtersAndListingsData?.allListings?.total + this.fallbackListings.length;

    if (!!this.props.location.query.page && this.props.location.query.page > 1) {
      return totalNonbumpedListings === 0;
    }

    return (totalNonbumpedListings + this.props.filtersAndListingsData?.allBumped?.listings.length === 0);
  }

  getBuyingGuideSlug() {
    if (this.props.layoutData?.loading || !this.csp) return null;

    return relatedBuyingGuide(
      this.csp?.brand?.name,
      this.csp.model,
      (this.csp?.categories || []),
      this.props.user,
    );
  }

  bumpedListings() {
    return this.listingsWithoutFeatured(ALL_BUMPED);
  }

  listingsWithoutFeatured(name: string) {
    const listings = this.getListings(name);

    if (!this.featuredListing()) return listings;

    const listingsWithoutFeatured = reject(listings, (listing) => {
      return listing.id === this.featuredListing().id;
    });

    return listingsWithoutFeatured.slice(0, LISTINGS_LIMIT - 1);
  }

  sponsoredListings() {
    if (isExperimentEnabled(this.props.user, experiments.CSP_FEATURED_LISTINGS_UPDATE)) {
      const eligibleSponsoredListings = this.eligibleSponsoredListings();

      const sponsoredListingIds = eligibleSponsoredListings.map(listing => listing.id);
      const totalSponsoredListings = eligibleSponsoredListings.length;
      const totalBumpedListingsToInclude = totalSponsoredListings > BUMP_LIMIT ? 0 : BUMP_LIMIT - totalSponsoredListings;

      const filteredBumpListings = this.bumpedListings().filter(listing => !sponsoredListingIds.includes(listing.id));

      return eligibleSponsoredListings.concat(
        filteredBumpListings.slice(0, totalBumpedListingsToInclude),
      ).slice(0, SPONSORED_LISTINGS_LIMIT);
    } else {
      return this.bumpedListings().slice(0, BUMP_LIMIT);
    }
  }

  showCspFeaturedInCollection() {
    return this.allNonBumpedListings().length <= SHOW_FEATURED_IN_COLLECTION_LIMIT;
  }

  allNonBumpedListings() {
    return [...this.getListings(ALL_TOGETHER), ...this.fallbackListings];
  }

  nonBumpedListings() {
    const idEqual = (a, b) => (a.id === b.id);

    const listingsToExclude = this.showCspFeaturedInCollection() ?
      this.sponsoredListings() :
      [...this.sponsoredListings(), ...[this.featuredListing()].filter(Boolean)];

    return differenceWith(this.allNonBumpedListings(), listingsToExclude, idEqual);
  }

  queryForAdditionalFeaturedListingFields() {
    return !this.promotedFeaturedListing();
  }

  promotedFeaturedListing() {
    if (this.hfid?.state === LISTING_STATES.LIVE) {
      return this.hfid;
    }

    if (this.buyBoxListing) {
      return this.buyBoxListing;
    }

    return null;
  }

  eligibleSponsoredListings() {
    const sponsored = this.props.filtersAndListingsData.cspSponsoredListing || {};
    const sponsoredSecondPlace = sponsored?.runnerUp || {};
    const sponsoredThirdPlace = sponsoredSecondPlace?.runnerUp || {};

    const sponsoredListings = [
      sponsored.listing,
      sponsoredSecondPlace.listing,
      sponsoredThirdPlace.listing,
    ];

    return sponsoredListings.filter(listing =>
      !!listing &&
      listing.id !== this.featuredListing()?.id,
    );
  }

  calculateFeaturedListing() {
    const promotedFeaturedListing = this.promotedFeaturedListing();

    if (promotedFeaturedListing) { return promotedFeaturedListing; }

    if (this.hasListings()) {
      return (this.getListings(ALL_BUMPED).length) ? this.getListings(ALL_BUMPED)[0] : this.getListings(ALL_TOGETHER)[0];
    }
  }

  featuredListing(): Listing {
    const cached = this.state.featuredListings[this.featuredListingKey()];
    return cached || this.calculateFeaturedListing();
  }

  get hfid(): CoreCspLayoutQuery.Hfid {
    return this.props.layoutData?.hfid;
  }

  get buyBoxListing(): CoreCspLayoutQuery.Listing {
    return this.props.layoutData?.featuredListing?.listing;
  }

  shouldRenderFretboardMaterialFilters() {
    return APPROVED_FRETBOARD_FILTER_CATEGORIES.includes(this.csp?.categories[0]?.parentCategories[0]?.id);
  }

  @bind
  onOfferSuccess(message) {
    flashSuccess(message);
  }

  trackListings() {
    trackEvent({
      eventName: MParticleEventName.ComponentView,
      entityType: 'CSP',
      entityId: this.csp?.id,
      listingsCount: this.allCurrentListingsBeingDisplayed().length,
      componentName: COMPONENT_NAME,
      experiments: formatExperimentsForEventAttributes(this.props.user, [
        experiments.DUMMY,
        experiments.MV_DUMMY,
      ]),
    });

    const listings = [].concat(this.featuredListing(), this.bumpedListings(), this.nonBumpedListings());
    const listingIds = uniq(listings.filter(l => !!l).map(l => l.id));

    trackCriteoProductList({
      listingIds,
    }, this.props.user);
    trackFacebookEvent(
      VIEW_CONTENT_EVENT,
      {
        content_ids: listingIds,
        content_type: PRODUCT_CONTENT_TYPE,
      },
    );
  }

  currentNonFeaturedListingsBeingDisplayed() {
    return union(this.sponsoredListings(), this.nonBumpedListings());
  }

  allCurrentListingsBeingDisplayed() {
    return compact(union(this.currentNonFeaturedListingsBeingDisplayed(), [this.featuredListing()]));
  }

  getFinishFilters(): CSP2FiltersOption[] {
    const finishAgg = this.props.filtersAndListingsData?.finishAgg?.aggregationResults[0]?.aggregationDetails || [];
    const totalByFinish = keyBy(finishAgg, 'key');
    const total = finish => (totalByFinish[finish]?.count || 0);

    const options = this.getFinishProductSpecs()
      .map((spec) => {
        return {
          name: spec.value,
          selected: this.isFinishFilterSelected(spec.value),
          total: total(spec.value),
          optionValue: spec.value,
          setValues: this.isFinishFilterSelected(spec.value) ? [] : [spec.value],
          unsetValues: this.isFinishFilterSelected(spec.value) ? [spec.value] : [],
          paramName: 'canonicalFinish',
        };
      });

    return options;
  }

  getFretboardMaterialFilters(): CSP2FiltersOption[] {
    if (!this.shouldRenderFretboardMaterialFilters()) return [];

    const fretboardMaterialAgg = this.props.filtersAndListingsData?.fretboardMaterialAgg?.aggregationResults[0]?.aggregationDetails || [];
    const totalByMaterial = keyBy(fretboardMaterialAgg, 'displayName');
    const total = material => (totalByMaterial[material]?.count || 0);

    const options = fretboardMaterialAgg
      .map(spec => {
        return {
          name: spec.displayName,
          selected: this.isFretboardMaterialSelected(spec.key),
          total: total(spec.displayName),
          optionValue: spec.key,
          setValues: this.isFretboardMaterialSelected(spec.key) ? [] : [spec.key],
          unsetValues: this.isFretboardMaterialSelected(spec.key) ? [spec.key] : [],
          paramName: 'traitValues',
        };
      });

    return options;
  }

  isFinishFilterSelected(finish) {
    return this.canonicalFinish() === finish;
  }

  isFretboardMaterialSelected(material) {
    return this.fretboardMaterial() === material;
  }

  getFinishProductSpecs() {
    return (this.props.layoutData?.csp?.specs?.productSpecs || [])
      .filter(spec => spec.type === core_apimessages_ProductSpec_Type.FINISH);
  }

  get filters() {
    const filters = this.props.filtersAndListingsData?.allListings?.filters || [];
    return listingsFilters(filters, this.props.user, this.props.location);
  }

  getListingsTruncationHeight() {
    const count = this.nonBumpedListings().length + this.sponsoredListings().length;
    if (count > LISTING_TRUNCATION_THRESHOLD) return LISTING_TRUNCATION_REM_HEIGHT;
    return null;
  }

  renderEmptyState() {
    if (this.showCspFeaturedInCollection() && this.allCurrentListingsBeingDisplayed().length) return null;
    if (this.sponsoredListings().length) return null;

    return (
      <CSP2EmptyState
        heading={I18n.t('discovery.csp2.notFound.heading')}
        followParams={followParams(this.props.params, this.props.location)}
        feedPrompt={I18n.t('discovery.csp2.notFound.feedPrompt')}
      />
    );
  }

  renderSidebar() {
    return (
      <CSP2Sidebar
        csp={this.csp}
        loading={this.props.layoutData?.loading}
        setJumplinks={this.setJumplinks}
        buyingGuideSlug={this.getBuyingGuideSlug()}
      />
    );
  }

  renderSimilarCsps() {
    return <SimilarCspsRow
      title={I18n.t('discovery.comparisonShoppingPages.similarCsps')}
      cspSlug={this.props.params.slug}
      onRender={() => this.setJumplinks({ [JUMPLINK_SIMILAR]: true })}
      id={JUMPLINK_SIMILAR}
    />;
  }

  renderHeader() {
    const soldListingId = qs.parse(
      this.props.location?.search,
      { ignoreQueryPrefix: true },
    ).ended_listing;

    return (
      <div>
        <ScrollTarget onlyScrollUp ref={el => { this.scrollTarget = el; }} />
        <CSP2Header
          csp={this.csp}
          slug={this.props.params.slug}
          soldListingId={soldListingId as any} // TODO: qs types are broken
        />
        <CSP2Metadata
          csp={this.csp}
          listings={this.allCurrentListingsBeingDisplayed()}
          loading={this.props.layoutData?.loading}
          condition={this.props.params.condition}
        />
      </div>
    );
  }

  renderFilterRow() {
    if (isUSA(this.props.user.countryCode)) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        entityType: 'CSP',
        entityId: this.csp?.id,
        componentName: COMPONENT_NAME,
        experiments: formatExperimentsForEventAttributes(this.props.user, [
          experiments.BEST_PRICE_LISTINGS_SORT_V2,
          experiments.LIKELIHOOD_TO_SELL_SORT_RESTART,
        ]),
      });
    }

    return (
      <div className="csp2-marketplace-filters">
        <div className="search-overview__primary">
          <div className="search-overview__count">
            <h3>{I18n.t('discovery.csp2.compareListings')}</h3>
          </div>
          <div className="search-overview__feed">
            <SortChip
              loading={this.props.filtersAndListingsData?.loading}
              sort={this.filters.find(filter => filter.widgetType === WidgetType.SORT)}
              trackingQuery=""
            />
          </div>
        </div>
        <div className="search-overview__secondary">
          <MarketplaceFilterRow
            filters={this.filters}
            loading={this.props.filtersAndListingsData?.loading}
            location={this.props.location}
            finishOptions={this.getFinishFilters()}
            fretboardMaterialOptions={this.getFretboardMaterialFilters()}
            hideOnMobile
            dynamicTitle
            displayCount
            showTraitFilters
          />
        </div>
      </div>
    );
  }

  renderReviewsAndSeoBlurb() {
    return (
      <div>
        <CSP2CSPReviews />
        <SeoBlurb
          title={I18n.t('discovery.comparisonShoppingPages.jumpLinks.blurb')}
          content={this.csp?.body}
          truncationHeight={150}
          setJumplinks={this.setJumplinks}
          name={JUMPLINK_BLURB}
        />
      </div>
    );
  }

  renderPriceGuideModule() {
    if (!this.csp?.id) return null;

    return (
      <CSPPriceGuideModuleContainer
        csp={this.csp}
        componentId={JUMPLINK_PRICEGUIDE}
      />
    );
  }

  renderJumplinks() {
    return (
      <>
        <CSP2Jumplinks
          user={this.props.user}
          csp={this.csp}
          {...this.getJumplinks()}
        />
      </>
    );
  }

  render() {
    return (
      <div
        className="dynamic-page dynamic-page--csp"
        itemScope
        itemType="http://schema.org/Product"
      >
        <AppInstallCallout
          calloutLocation="csp"
          canonicalUrl={withWebHost(Paths.csp.expand({ cspSlug: this.props.params.slug }))}
        />
        {this.renderHeader()}
        {this.renderJumplinks()}
        <div className="dynamic-page__content">
          {(this.props.layoutData?.loading || this.featuredListing()) &&
            <CSP2FeaturedListing
              listing={this.featuredListing()}
              csp={this.csp}
              isLoading={this.props.layoutData?.loading}
              isBumped={!!this.featuredListing()?.bumped && !this.hfid}
              loggedOut={this.props.user?.loggedOut}
              queryForAdditionalFields={this.queryForAdditionalFeaturedListingFields()}
            />
          }
          <div className="dynamic-page__two-col">
            <div
              className="dynamic-page__two-col__main"
            >
              <SellYoursV4 csp={this.csp} belowListing />
              {this.renderFilterRow()}
              {!!this.fallbackListings.length &&
                <div className="mb-2">
                  <RCAlertBox type="warning">
                    {I18n.t('discovery.csp2.viewingFallbackListings')}
                  </RCAlertBox>
                </div>
              }
              <TruncationContainer
                remMaxHeight={this.getListingsTruncationHeight()}
                trackingName={TRUNCATION_CONTAINER_TRACKING_NAME}
              >
                <ListingsCollectionClass
                  jumplinkSlug={JUMPLINK_LISTINGS}
                  listings={this.nonBumpedListings()}
                  sponsoredListings={this.sponsoredListings()}
                  componentName={COMPONENT_NAME}
                  loading={this.props.filtersAndListingsData?.loading}
                  emptyState={this.renderEmptyState()}
                  onOfferSuccess={this.onOfferSuccess}
                  hideMetadataAsOffer={!!this.hfid}
                  displayAsRowCards
                  displayGreatValue
                  showEstimatedMonthlyPayment={canViewFinancingOptions(this.props.user)}
                  cspFeaturedListingId={this.featuredListing()?.id}
                />
                <RQLPagination
                  limit={LISTINGS_LIMIT}
                  total={this.props.filtersAndListingsData?.allListings?.total || 0}
                  offset={this.props.filtersAndListingsData?.allListings?.offset || 0}
                  anchor="listings"
                  paginationWindow={2}
                  replaceHistory
                />
              </TruncationContainer>
              <div className="mtb-2">
                <CSPProductFamilyCTA
                  csp={this.csp}
                />
              </div>
              {
                !!this.currentNonFeaturedListingsBeingDisplayed().length &&
                <CSP2FeedCallout
                  followParams={followParams(this.props.params, this.props.location)}
                />
              }
            </div>
            <div className="dynamic-page__sidebar">
              {this.renderSidebar()}
            </div>
          </div>
          <div className="dynamic-page__one-col">
            {this.renderSimilarCsps()}
            <RecentlyViewedCspsRow />
            {this.renderPriceGuideModule()}
            {this.renderReviewsAndSeoBlurb()}
          </div>
        </div>
      </div>
    );
  }
}

const fragments = {
  listing: gql`
    fragment ListingData on Listing {
      _id
      upc
      finish
      model
      shopUuid
      ...SellerInfoData
    }
    ${SellerInfoFragment}
  `,
};

const withLayoutQuery = withGraphql<ExternalProps, CoreCspLayoutQuery.Query>(
  gql`
    query Core_Csp_LayoutQuery(
      $auctionType: arbiter_AuctionType
      $canonicalFinish: String
      $cspSlug: String
      $hfid: String
      $hfidPresent: Boolean!
      $includeFullFinancingFields: Boolean!
      $itemRegion: String
      $shippingRegionCode: String
      $shouldSkipTracking: Boolean!
      $showListingLocation: Boolean!
      $traitValues: [String]
      $userCurrency: String
    ) {
      csp(
        input: {slug: $cspSlug}
      ) {
        _id
        id
        title
        summary
        body
        metaDescription
        model
        generic
        finishes
        brand {
          _id
          name
          slug
          webLink {
            href
          }
        }
        categories {
          _id
          id
          name
          slug
          webLink {
            href
          }
          cmsUrl {
            href
          }
          parentCategories {
            _id
            id
            name
            slug
            rootSlug
            webLink {
              href
            }
            cmsUrl {
              href
            }
          }
        }
        videos {
          videoLinks {
            youtubeLink
            youtubeVideoId
          }
        }
        specs {
          productSpecs {
            type
            name
            path
            value
          }
        }
        supersize_images: images(input:{
          transform: "supersize"
          scope: "photos"
          limit: 20
        }) {
          source
        }
        large_images: images(input:{
          transform: "large"
          scope: "photos"
          limit: 20
        }) {
          source
        }
        hero_image: images(input:{
          transform: "banner_wide"
          scope: "hero_image"
          limit: 1
        }) {
          source
        }
        cspImage: images(input: {
          transform: "card_square"
          scope: "photos"
          limit: 1
        }) {
          source
        }
        variants {
          id
        }
        ... CSPAdditionalTrackingFields
        ... CSPReviewJumplinksData
        ... FeaturedListingCSP
        ... CSPProductFamilyCTAData
      }
      featuredListing: featuredListing(
        input: {
          cspSlug: $cspSlug
          userShippingRegion: $shippingRegionCode
          canonicalFinish: $canonicalFinish
          traitValues: $traitValues
          itemRegion: $itemRegion
          auctionType: $auctionType
          userCurrency: $userCurrency
        }
      ) {
        listing {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... ShopFields @include(if: $showListingLocation)
          ... BumpKey
          ... FeaturedListingWithPrice
        }
      }
      hfid: listing(
        input: {
          id: $hfid
        }
      ) @include(if: $hfidPresent) {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... BumpKey
        ... FeaturedListingWithPrice
      }
    }
    ${ListingsCollectionClass.fragments.listing}
    ${fullFinancingFieldsFragment}
    ${ListingsCollectionClass.fragments.bumpKey}
    ${CSPAdditionalTrackingFieldsFragment}
    ${CSPJumplinksFragment}
    ${CSP2FeaturedListingFragment.listing}
    ${CSP2FeaturedListingFragment.csp}
    ${ListingsCollectionClass.fragments.shopFields}
    ${CSPProductFamilyCTAFragment}
    ${mParticleListingFields}
    ${fragments.listing}
  `,
  {
    name: 'layoutData',
    options: (ownProps) => {
      return {
        ssr: true,
        // allows for partial failures in RQL response
        errorPolicy: 'all',
        variables: {
          admin: !!ownProps.user?.isAdmin,
          auctionType: arbiter_AuctionType.CSP,
          canonicalFinish: (ownProps.location?.query?.canonicalFinish || ''),
          canonicalFinishes: compact([ownProps.location?.query?.canonicalFinish]),
          cspSlug: ownProps.params.slug,
          hfid: (ownProps.location?.query?.hfid || ''),
          hfidPresent: !!ownProps.location?.query?.hfid,
          includeFullFinancingFields: canViewFinancingOptions(ownProps.user),
          itemRegion: localizeCSPItemRegion(ownProps.location?.query?.item_region, ownProps.user),
          loggedOut: !!ownProps.user?.loggedOut,
          shippingRegionCode: ownProps.user?.shippingRegionCode,
          shouldSkipTracking: !userContextIsTrackable(ownProps.user),
          showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
          traitValues: compact([(ownProps.location?.query?.traitValues || ownProps.location?.query?.trait_values || '')]).flat(),
          userCurrency: ownProps.user?.currency,
          showBestPriceListingsSort: isUSA(ownProps.user.countryCode) && isExperimentEnabled(ownProps.user, experiments.BEST_PRICE_LISTINGS_SORT_V2),
          showLikelihoodToSellSort: isUSA(ownProps.user.countryCode) && isExperimentEnabled(ownProps.user, experiments.LIKELIHOOD_TO_SELL_SORT_RESTART),
        },
      };
    },
  },
);

const withFiltersAndListingsQuery = withGraphql<ExternalProps, CspFiltersAndListingsQuery.Query>(
  gql`
    query Csp_Filters_And_ListingsQuery(
      $acceptsAffirm: Boolean
      $acceptsGiftCards: Boolean
      $acceptsOffers: Boolean
      $acceptsPaymentPlans: Boolean
      $aggs: [reverb_search_ListingsSearchRequest_Aggregation]
      $bumpLimit: Int
      $dealsAndSteals: Boolean
      $decades: [String]
      $canonicalFinishes: [String]
      $categorySlugs: [String]
      $combinedShipping: Boolean
      $conditionSlugs: [String]
      $cspSlug: String
      $excludeLocalPickupOnly: Boolean
      $fallbackToEverywhereElse: Boolean
      $freeExpeditedShipping: Boolean
      $freeShipping: Boolean
      $fretboardMaterial: String
      $includeFullFinancingFields: Boolean!
      $itemRegion: String
      $itemRegionRelation: reverb_search_ListingItemRegionRelation
      $limit: Int
      $localPickup: Boolean
      $offset: Int
      $onSale: Boolean
      $preferredSeller: Boolean
      $priceMin: String
      $priceMax: String
      $query: String
      $shippingRegionCode: String
      $shippingRegionCodes: [String]
      $shouldHideBumpAndSponsoredListings: Boolean!
      $shouldSkipTracking: Boolean!
      $showBestPriceListingsSort: Boolean
      $showLikelihoodToSellSort: Boolean
      $sort: reverb_search_ListingsSearchRequest_Sort
      $sortSlug: String
      $sponsoredConditionSlugs: [String]
      $traitValues: [String]
      $withProximityFilter: Input_reverb_search_ProximityFilterRequest
      $yearMax: String
      $yearMin: String
      $userCurrency: String
    ) {
      allListings: listingsSearch(
        input: {
          cspSlug: $cspSlug
          conditionSlugs: $conditionSlugs
          statuses: ["live"]
          canonicalFinishes: $canonicalFinishes
          traitValues: $traitValues
          withAggregations: $aggs
          priceMin: $priceMin
          priceMax: $priceMax
          query: $query
          categorySlugs: $categorySlugs
          sort: $sort
          sortSlug: $sortSlug
          localPickup: $localPickup
          acceptsOffers: $acceptsOffers
          onSale: $onSale
          excludeLocalPickupOnly: $excludeLocalPickupOnly
          freeShipping: $freeShipping
          freeExpeditedShipping: $freeExpeditedShipping
          combinedShipping: $combinedShipping
          acceptsPaymentPlans: $acceptsPaymentPlans
          dealsAndSteals: $dealsAndSteals
          decades: $decades
          yearMax: $yearMax
          yearMin: $yearMin
          acceptsGiftCards: $acceptsGiftCards
          preferredSeller: $preferredSeller
          acceptsAffirm: $acceptsAffirm
          itemRegion: $itemRegion
          itemRegionRelation: $itemRegionRelation
          shippingRegionCodes: $shippingRegionCodes
          withProximityFilter: $withProximityFilter
          fallbackToEverywhereElse: $fallbackToEverywhereElse
          showBestPriceListingsSort: $showBestPriceListingsSort
          showLikelihoodToSellSort: $showLikelihoodToSellSort
          offset: $offset
          limit: $limit
        }
      ) {
        total
        offset
        filters {
          ...NestedFilter
        }
        listings {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... ListingGreatValueData
        }
        fallbackListings {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... ListingGreatValueData
        }
      }
      allBumped: bumpedSortedListings(
        input: {
          cspSlug: $cspSlug
          conditionSlugs: $conditionSlugs
          statuses: ["live"]
          sort: PRICE_ASC
          filterSuperRegionCode: $shippingRegionCode
          bumpedOnly: true
          canonicalFinishes: $canonicalFinishes
          traitValues: $traitValues
          withAggregations: $aggs
          priceMin: $priceMin
          priceMax: $priceMax
          query: $query
          categorySlugs: $categorySlugs
          localPickup: $localPickup
          acceptsOffers: $acceptsOffers
          onSale: $onSale
          freeShipping: $freeShipping
          freeExpeditedShipping: $freeExpeditedShipping
          combinedShipping: $combinedShipping
          acceptsPaymentPlans: $acceptsPaymentPlans
          dealsAndSteals: $dealsAndSteals
          decades: $decades
          yearMax: $yearMax
          yearMin: $yearMin
          acceptsGiftCards: $acceptsGiftCards
          preferredSeller: $preferredSeller
          acceptsAffirm: $acceptsAffirm
          shippingRegionCodes: $shippingRegionCodes
          withProximityFilter: $withProximityFilter
          currency: $userCurrency
          bumpedSortedListingsQuery: {
            condition: "all"
            bumpedMax: $bumpLimit
            total: $bumpLimit
            itemRegion: $itemRegion
          }
        }
      ) @skip (if: $shouldHideBumpAndSponsoredListings) {
        listings {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... BumpKey
        }
      }
      cspSponsoredListing(input: {
        cspSlug: $cspSlug
        conditionSlugs: $sponsoredConditionSlugs
        statuses: ["live"]
        sort: PRICE_ASC
        canonicalFinishes: $canonicalFinishes
        traitValues: $traitValues
        priceMin: $priceMin
        priceMax: $priceMax
        query: $query
        categorySlugs: $categorySlugs
        localPickup: $localPickup
        acceptsOffers: $acceptsOffers
        onSale: $onSale
        freeShipping: $freeShipping
        freeExpeditedShipping: $freeExpeditedShipping
        combinedShipping: $combinedShipping
        acceptsPaymentPlans: $acceptsPaymentPlans
        dealsAndSteals: $dealsAndSteals
        decades: $decades
        yearMax: $yearMax
        yearMin: $yearMin
        acceptsGiftCards: $acceptsGiftCards
        preferredSeller: $preferredSeller
        acceptsAffirm: $acceptsAffirm
        shippingRegionCodes: $shippingRegionCodes
        withProximityFilter: $withProximityFilter
        itemRegion: $itemRegion
        itemRegionRelation: $itemRegionRelation
        currency: $userCurrency
      }) @skip (if: $shouldHideBumpAndSponsoredListings) {
        listing {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... BumpKey
          ... FeaturedListingWithPrice
        }
        runnerUp {
          listing {
            _id
            ... ListingData
            ... ListingsCollection
            ... mParticleListingFields @skip(if: $shouldSkipTracking)
            ... FullFinancingFields @include(if: $includeFullFinancingFields)
            ... BumpKey
            ... FeaturedListingWithPrice
          }
          runnerUp {
            listing {
              _id
              ... ListingData
              ... ListingsCollection
              ... mParticleListingFields @skip(if: $shouldSkipTracking)
              ... FullFinancingFields @include(if: $includeFullFinancingFields)
              ... BumpKey
              ... FeaturedListingWithPrice
            }
          }
        }
      }
      finishAgg: listingsAggregationSearch(input: {
        withAggregations: CANONICAL_FINISH
        cspSlug: $cspSlug
      }) {
        aggregationResults {
          name
          aggregationDetails {
            key
            count
          }
        }
      }
      fretboardMaterialAgg: listingsAggregationSearch(input: {
        canonicalFinishes: $canonicalFinishes
        withAggregations: TRAITS
        cspSlug: $cspSlug
        traitValues: [$fretboardMaterial]
      }) {
        aggregationResults {
          name
          displayName
          aggregationDetails {
            key
            displayName
            count
          }
        }
      }
    }
    ${ListingsCollectionClass.fragments.listing}
    ${fragments.listing}
    ${nestedFilterFragment}
    ${mParticleListingFields}
    ${fullFinancingFieldsFragment}
    ${ListingsCollectionClass.fragments.bumpKey}
    ${ListingGreatValueFragment}
    ${CSP2FeaturedListingFragment.listing}
  `,
  {
    name: 'filtersAndListingsData',
    options: (ownProps) => {
      const queryParams = ownProps.location?.query || {};

      const aggs = [
        Aggregation.CATEGORY_SLUGS,
        Aggregation.CONDITION_SLUGS,
        Aggregation.DECADES,
        Aggregation.TRAITS,
      ];

      function shippingRegionCodes() {
        if (queryParams.ships_to) {
          return compact([queryParams.ships_to]);
        }

        if (ownProps.user?.shippingRegionCode) {
          return compact([ownProps.user?.shippingRegionCode]);
        }

        return null;
      }

      return {
        ssr: false,
        errorPolicy: 'all',
        variables: {
          acceptsAffirm: queryParams.accepts_affirm,
          acceptsGiftCards: queryParams.accepts_gift_cards,
          acceptsOffers: queryParams.accepts_offers,
          acceptsPaymentPlans: queryParams.zero_percent_financing,
          aggs,
          bumpLimit: BUMP_LIMIT + 1,
          canonicalFinishes: compact([queryParams.canonicalFinish]),
          categorySlugs: ownProps.location.query?.category_slugs,
          combinedShipping: queryParams.combined_shipping,
          conditionSlugs: compact(castArray(queryParams.condition)),
          cspSlug: ownProps.params.slug,
          dealsAndSteals: queryParams.deals_and_steals,
          decades: queryParams.decades,
          excludeLocalPickupOnly: queryParams.exclude_local_pickup_only,
          fallbackToEverywhereElse: localizeCSPForUser(ownProps.user),
          freeExpeditedShipping: queryParams.free_expedited_shipping,
          freeShipping: queryParams.free_shipping,
          fretboardMaterial: FRETBOARD_MATERIAL_TRAIT_UUID,
          includeFullFinancingFields: canViewFinancingOptions(ownProps.user),
          itemRegion: localizeCSPItemRegion(queryParams.item_region, ownProps.user),
          limit: LISTINGS_LIMIT,
          localPickup: queryParams.local_pickup,
          offset: getOffsetFromPage(
            queryParams.page,
            LISTINGS_LIMIT,
          ),
          onSale: queryParams.on_sale,
          preferredSeller: queryParams.preferred_seller,
          priceMin: ownProps.location.query.price_min,
          priceMax: ownProps.location.query.price_max,
          query: queryParams.query,
          shippingRegionCode: ownProps.user?.shippingRegionCode,
          shippingRegionCodes: shippingRegionCodes(),
          shouldHideBumpAndSponsoredListings: hideBumpedAndSponsoredListings({
            pageNumber: queryParams.page,
            sortQuery: queryParams.sort,
          }),
          shouldSkipTracking: !userContextIsTrackable(ownProps.user),
          showBestPriceListingsSort: isUSA(ownProps.user.countryCode) && isExperimentEnabled(ownProps.user, experiments.BEST_PRICE_LISTINGS_SORT_V2),
          showLikelihoodToSellSort: isUSA(ownProps.user.countryCode) && isExperimentEnabled(ownProps.user, experiments.LIKELIHOOD_TO_SELL_SORT_RESTART),
          sort: reverb_search_ListingsSearchRequest_Sort.NONE,
          sortSlug: queryParams.sort,
          sponsoredConditionSlugs: queryParams.condition ? compact(castArray(queryParams.condition)) : [CONDITION_GROUPS.new],
          traitValues: compact([(queryParams.traitValues || queryParams.trait_values || '')]).flat(),
          userCurrency: ownProps.user?.currency,
          yearMin: queryParams.year_min,
          yearMax: queryParams.year_max,
          withProximityFilter: {
            proximity: toBool(queryParams.proximity),
            distance: queryParams.distance,
            postalCode: queryParams.postal_code,
          },
        },
      };
    },
  },
);

export default connect([
  withUserContext,
  withRouter,
  withLayoutQuery,
  withFiltersAndListingsQuery,
])(CSP2Layout);
